import React, { useState } from 'react';
import styled from '@emotion/styled';
import ContentLoader from 'ui_system/utilities/ContentLoader';
import { colors } from 'ui_system/compass';
import { t } from 'services/translation';
import { Theme } from 'ui_system/theme';
import Dropdown from './dropdown_select/Dropdown';

export const Container = styled.span`
  display: table;
  color: ${colors.border};
  font-size: 14px;
  cursor: ${(props) => (props.isOpenable ? 'pointer' : 'cursor')};
  position: relative;
  z-index: 1;
  width: ${(props) => (props.customDropdown ? '75%' : '100%')};
`;

const Row = styled.div`
  display: table-row;
`;

const Cell = styled.div`
  display: table-cell;
`;

const DropdownCell = styled(Cell)`
  width: ${(props) => (props.customDropdown ? '40px' : '100%')};
`;
const ToggleCell = styled(Cell)`
  width: 15px;
  padding-left: 10px;
  margin-right: 2px;
  ${(props) =>
    props.customDropdown ? 'padding-left: 10px' : 'padding-right: 10px'};
`;

const PrimaryCell = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  text-align: center;
  flex-direction: column;
  padding: 4px;
  color: #6e6e6e;
  margin-bottom: 3px;
  font-size: 12px;
  line-height: 16px;
  width: 56px;
  border-radius: 6px;
  background-color: #e6e6e6;
`;

const DropdownLabel = styled.div`
  margin-right: -5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedLabel = styled(DropdownLabel)`
  font-size: 14px;
  max-width: 150px;
  color: ${Theme.colors.black};
  padding-left: ${(props) => (props.customDropdown ? '0px' : '18px')};
`;

export const Item = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto auto auto;
  padding-left: 15px;
  padding-right: 15px;

  background-color: ${(props) =>
    props.selected && props.customDropdown
      ? 'rgba(0, 113, 188, 0.06)'
      : 'unset'};
  &:hover {
    color: ${(props) =>
      props.customDropdown ? '#ffffff' : colors.text.primary};
    background-color: ${(props) =>
      props.isOpenable && props.customDropdown
        ? props.brandcolor
        : props.isOpenable && !props.customDropdown
          ? colors.background.active
          : 'unset'};
  }

  height: 50px;
  border-bottom: ${(props) => (props.last ? '0px' : '1px solid #cecece')};
  align-items: center;

  /* Needed for Mobile safari */
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    border-bottom: none;
  }
`;

const SelectedItem = styled.div`
  height: 100%;
  width: 100%;
  color: ${(props) =>
    props.customDropdown ? props.brandcolor : colors.text.primary};
  font-size: 16px;
  border-bottom: 0px;
  padding-left: 0px;

  &:hover {
    color: ${(props) =>
      props.customDropdown ? props.brandcolor : colors.text.primary};
  }
`;

const ToggleIcon = styled.div`
  /* CSS Triangles! */
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 7.7px 6px 0 6px;
  border-color: ${(props) =>
      props.customDropdown ? props.brandcolor : colors.text.icon}
    transparent transparent transparent;
`;

const OpenParenthesis = styled(Cell)`
  width: 5px;
`;

const ClosedParenthesis = styled(Cell)`
  width: 5px;
  padding-right: 10px;
`;

const TextSelect = ({
  items,
  onSelectItem,
  flexibleHeight,
  selectedValue,
  selectedLabel,
  isOpenable,
  dataTestId,
  brandColor,
  unitsSelectOptions,
  customDropdown,
  type,
}) => {
  const [open, setOpen] = useState(false);
  const brandingColor = brandColor || colors.primary;
  const shouldShowDropdown = !(
    customDropdown && unitsSelectOptions?.length <= 1
  );

  // Auto-select single item if available
  React.useEffect(() => {
    if (items?.length === 1 && !selectedValue && onSelectItem) {
      onSelectItem(items[0].value);
    }
  }, [items, selectedValue, onSelectItem]);

  // Items should have { label: string, value: string }
  const renderItems = () => {
    if (!items) return null;
    const itemsLength = items.length;
    return items.map((item, index) => {
      const { label, value } = item;

      return (
        <Item
          className="primary-guest-selection-item"
          key={value}
          title={label}
          onClick={() => {
            setOpen(false);
            onSelectItem(value);
          }}
          last={index + 1 === itemsLength}
          isOpenable={open}
          brandcolor={brandingColor}
          customDropdown={customDropdown}
        >
          <DropdownLabel>{label}</DropdownLabel>
        </Item>
      );
    });
  };

  return (
    <Container
      isOpenable={isOpenable}
      flexibleHeight={flexibleHeight}
      data-testid={dataTestId}
    >
      <Row>
        {shouldShowDropdown && (
          <>
            {customDropdown && <OpenParenthesis>(</OpenParenthesis>}
            <DropdownCell customDropdown={customDropdown}>
              <SelectedItem
                className="primary-guest-selection-selected-item"
                customDropdown={customDropdown}
                onClick={() => setOpen(!open)}
                brandcolor={brandingColor}
              >
                <SelectedLabel
                  className="primary-guest-ticket-label"
                  brandcolor={brandingColor}
                  customDropdown={customDropdown}
                  data-testid="primary-guest-ticket-label"
                >
                  {selectedLabel !== null ? selectedLabel : 'Select'}
                </SelectedLabel>
              </SelectedItem>
              <Dropdown
                open={open}
                setOpen={(isOpen) => setOpen(isOpen)}
                items={renderItems()}
                customdropdown={customDropdown}
                topValue={type === 'add' ? '25.4%' : '28.6%'}
                topborder
                forgog
              />
            </DropdownCell>
            <ToggleCell
              onClick={() => setOpen(!open)}
              customDropdown={customDropdown}
            >
              <ToggleIcon
                data-testid="toggle-dropdown"
                brandcolor={brandingColor}
                customDropdown={customDropdown}
              />
            </ToggleCell>
            {customDropdown && <ClosedParenthesis>)</ClosedParenthesis>}
          </>
        )}
        {customDropdown && (
          <PrimaryCell className="primary-guest-tag">
            {t('PRIMARY')}
          </PrimaryCell>
        )}
      </Row>
    </Container>
  );
};

export default TextSelect;
